import { useWorkspaceInfo } from '@modules/workspace/providers/WorkspaceInfo';
import { useWorkspaceInfoActiveUsers } from '@modules/workspace/providers/WorkspaceInfo/hooks/useWorkspaceInfoActiveUsers';
import { useWorkspaceInfoTeamsById } from '@modules/workspace/providers/WorkspaceInfo/hooks/useWorkspaceInfoTeamsById';
import { useWorkspaceInfoUsersById } from '@modules/workspace/providers/WorkspaceInfo/hooks/useWorkspaceInfoUsersById';
import { useMemo } from 'react';
import {
  IWorkspaceTeam,
  IWorkspaceUser,
  UserType,
  WorkspaceParticipant,
} from 'src/@types/workspace-info';

import { useSelector } from 'src/redux/store';

export const useWorkspaceUser = (userId: string) => {
  const { usersById } = useWorkspaceInfoUsersById();
  return usersById[userId];
};

export const useWorkspaceTeam = (teamId: string) => {
  const { teamsById } = useWorkspaceInfoTeamsById();
  return teamsById[teamId];
};

export const useWorkspaceChannel = (channelId: string) =>
  useSelector((state) => state.chat.conversations.byId[channelId]);

export const useWorkspaceRealMembers = () => {
  const { activeUsers } = useWorkspaceInfoActiveUsers();
  return activeUsers.filter((user) => user.type === UserType.USER);
};

export const useWorkspaceParticipantsUsers = (
  participants: WorkspaceParticipant[],
): IWorkspaceUser[] => {
  const { usersById } = useWorkspaceInfoUsersById();
  return useMemo(
    () =>
      participants
        ?.map((p) => 'user' in p && usersById[p.user])
        .sort((a, b) => {
          if (!a || !b) return 0;
          if (a.name < b.name) return -1;
          return 0;
        })
        .filter(Boolean) as IWorkspaceUser[],
    [participants, usersById],
  );
};

export const checkParticipantIsUser = (
  participant: WorkspaceParticipant,
  userId: string,
) => 'user' in participant && participant.user === userId;

export const useWorkspaceParticipants = (
  participants: WorkspaceParticipant[],
) => {
  const { usersById } = useWorkspaceInfoUsersById();
  const { teamsById } = useWorkspaceInfoTeamsById();

  return useMemo(
    () =>
      participants
        .map((participant) => {
          if ('user' in participant)
            return usersById[participant.user] as IWorkspaceUser;

          if ('team' in participant)
            return teamsById[participant.team] as IWorkspaceTeam;

          return null;
        })
        .filter(Boolean) as (IWorkspaceUser | IWorkspaceTeam)[],
    [participants, usersById, teamsById],
  );
};

export const useWorkspaceParticipantsAsUsers = (
  participants: WorkspaceParticipant[],
  realMembers = false,
) => {
  const { usersById } = useWorkspaceInfoUsersById();
  const { teamsById } = useWorkspaceInfoTeamsById();

  return useMemo(() => {
    const workspaceParticipants = [
      ...new Set(
        participants
          .flatMap((participant) => {
            if ('user' in participant) return [usersById[participant.user]];
            if ('team' in participant)
              return teamsById[participant.team]?.participants.map(
                (p) => usersById[p],
              );
            return [];
          })
          .sort((a, b) => {
            if (!a || !b) return 0;
            if (a.name < b.name) return -1;
            return 0;
          })
          .filter(Boolean) as IWorkspaceUser[],
      ),
    ];
    if (realMembers) {
      return workspaceParticipants.filter(
        (user) => user.type === UserType.USER && !!user.active,
      );
    }
    return workspaceParticipants;
  }, [participants, usersById, teamsById, realMembers]);
};

export const useWorkspaceFeaturesSettings = () => {
  const { workspace } = useWorkspaceInfo();
  const { smartTodos } = workspace.featureSettings;
  return { smartTodos };
};
